// All copy for the public site. Hardcoded on purpose: every public page in this app is
// already static, and the source project's CMS (eleven Prisma models on Postgres) is
// deliberately not ported. Component props mirror the source's shapes exactly, so the
// components port mechanically and a CMS could be dropped in later without touching them.

import Work1 from '../images/public2/2.jpg';
import Work2 from '../images/public2/3.jpg';
import Work3 from '../images/public2/4.jpg';
import Work4 from '../images/public2/5.jpg';

// Photographs for the /services page (Task 8) - the only per-service imagery on the site,
// carried over verbatim from src/pages/public/service/{aligners,dentalImplants,crownsbridges,
// laminates,Partialdenter,aorthdontix}.jsx, which each imported two of these directly.
// Centralised here (same pattern as Work1-4 above) since the six service components now read
// their copy AND images from one `serviceDetails` export rather than duplicating image imports.
import AlignersA from '../images/public/17.jpg';
import AlignersB from '../images/public/28.jpg';
import ImplantsA from '../images/public/19.jpg';
import ImplantsB from '../images/public/20.webp';
import CnbA from '../images/public/21.jpg';
import CnbB from '../images/public/22.jpg';
import VeneersA from '../images/public/23.avif';
import VeneersB from '../images/public/24.webp';
import DenturesA from '../images/public/26.jpg';
import DenturesB from '../images/public/27.jpg';
import OrthodontixB from '../images/public/28.png';

export const settings = {
    brandName: 'N Vision',
    brandSub: 'Digital Dental Laboratory',
    logoMark: 'NV',
    // Real routes, not the source's one-page anchors (#services): this app has seven pages.
    // '/contant' is spelled that way on purpose - it is the live route in src/App.js.
    navLinks: [
        { label: 'Services',      to: '/services' },
        { label: 'How it works',  to: '/about' },
        { label: 'Client portal', to: '/login' },
        { label: 'Contact',       to: '/contant' }
    ],
    contact: {
        email: 'info@nvisiondental.com',
        phone: '+92 (300) 460 - 1314',
        addressLine: 'Charlotte · Lahore · Faisalabad'
    }
};

// Keyed by section, each {eyebrow, title, subtitle, content} - verbatim from the seed's
// `sections` array, reshaped from an array of {key, ...} rows into a map keyed by `key`.
export const sections = {
    hero: {
        eyebrow: 'Digital Dental Laboratory',
        title: 'Precision restorations, delivered fast',
        subtitle:
            'CAD/CAM crowns, bridges, implants, and veneers crafted to the exact specifications your patients deserve — with digital case submission and real-time order tracking.',
        content: {
            titleLines: ['Precision', 'restorations,', 'delivered fast'],
            emphasisFrom: 2,
            primaryCta: 'Submit a case',
            secondaryCta: 'Request fee schedule'
        }
    },
    services: {
        eyebrow: 'Our services',
        title: 'Every restoration, digitally crafted',
        subtitle:
            "Digital precision with a technician's eye: every case is designed in 3Shape or exocad, then stained, glazed and checked by our team before it reaches your practice."
    },
    process: {
        eyebrow: 'Digital workflow',
        title: 'From scan to delivery in four steps'
    },
    portal: {
        eyebrow: 'Client portal',
        title: 'Manage every case from one dashboard',
        subtitle:
            'No more phone tag. Submit cases, upload scans, track production status, and download invoices — all in one place, any time.',
        content: {
            primaryCta: 'Access portal',
            secondaryCta: 'Request account access',
            // Animated laptop illustration (PortalIllustration.jsx): the portal showing a crown
            // in CAD review. Replaced a dashboard card of made-up figures (a named doctor, case
            // counts, turnaround, balance) that read as real data.
            illustration: {
                appName: 'N Vision Portal',
                caseLabel: 'Zirconia crown · CAD design',
                callout: 'Margin marked',
                toastTitle: 'Design ready for approval',
                toastSub: 'Review it in your portal'
            }
        }
    },
    testimonials: {
        eyebrow: 'From our clients',
        title: 'Trusted by dental practices'
    },
    addresses: {
        eyebrow: 'Our locations',
        title: 'Where to find us'
    },
    finalCta: {
        eyebrow: 'Get started today',
        title: 'Ready to work with a lab that keeps up with you?',
        subtitle:
            'Submit your first case, request a fee schedule, or just call us to talk through your requirements — no commitment needed.',
        content: {
            primaryCta: 'Submit a case',
            secondaryCta: 'Request fee schedule',
            callCta: 'Call us'
        }
    }
};

// Task 7 (About): copy recovered verbatim from the pre-redesign
// src/pages/public/about/{hero,craftsmanship,innovative,personalized,quality,experience}/
// index.jsx - not one word changed. This page has no counterpart in the ported design (the
// source project never had an About page), so unlike `sections` above, none of this was
// seeded from Front End V3.html - it is the site's own existing copy, reshaped to fit the
// primitives the rest of this file already uses.
//
// The five essays are nothing but an <h3> + one ~90-word <p> each - no list data, so unlike
// `services`/`steps`/`features` they don't carry icons or tags. `eyebrow` on both the hero
// and each essay reuses `sections.hero.eyebrow` verbatim ('Digital Dental Laboratory') rather
// than inventing new per-section labels: the task's content rule forbids new headings/copy,
// and the five sections are parallel claims, not a sequence, so a shared non-distinguishing
// kicker (already used identically on the Home hero pill) fits better than five invented ones.
export const about = {
    hero: {
        eyebrow: sections.hero.eyebrow,
        title: 'About N-Vision Digital Dental Laboratory',
        paragraph:
            'At N-Vision Digital Dental Laboratory, we redefine smiles with precision and innovation. Our unwavering commitment to crafting dental perfection has made us a trusted leader in the field of digital dentistry. With a blend of cutting-edge technology, artistic expertise, and a passion for patient satisfaction, we create dental restorations that not only restore function but also enhance the beauty of every smile.',
        founderName: 'Adil Akram',
        founderRole: '(Founder & CEO)',
        primaryCta: 'Become a member',
        secondaryCtaLead: 'Already have an account',
        secondaryCtaLabel: 'Log in',
        // Same five profiles/URLs as Footer.jsx's SOCIAL_LINKS (icon glyphs match Footer's
        // square variants too, for visual consistency between the two social rows) - kept
        // as plain data here (icon key, not JSX) since content/site.js holds data, not
        // components; the About hero maps `icon` to a FontAwesome brand icon itself.
        social: [
            { order: 0, icon: 'facebook', label: 'Facebook', href: 'https://www.facebook.com/profile.php?id=100093019306708' },
            { order: 1, icon: 'x', label: 'X (Twitter)', href: 'https://twitter.com/Nvisiondental' },
            { order: 2, icon: 'linkedin', label: 'LinkedIn', href: 'https://pk.linkedin.com/in/n-vision-dental-3b206b279' },
            { order: 3, icon: 'instagram', label: 'Instagram', href: 'https://www.instagram.com/nvisiondental/' },
            { order: 4, icon: 'youtube', label: 'YouTube', href: 'https://www.youtube.com/channel/UCqYMbk_6ktRqoDcNR1xxYtw' }
        ]
    },
    // Five parallel claims, not a sequence - no 01/02/03 numbering (task-7 brief).
    essays: [
        {
            order: 0,
            key: 'craftsmanship',
            title: 'Craftsmanship Meets Technology',
            paragraph:
                'Our laboratory stands at the intersection of traditional craftsmanship and state-of-the-art technology. With a team of highly skilled dental artisans and technicians, combined with the latest digital tools, we are dedicated to pushing the boundaries of dental aesthetics and functionality. We believe that each dental restoration is a work of art, and our meticulous attention to detail ensures that every piece leaving our laboratory is a testament to our commitment to excellence.'
        },
        {
            order: 1,
            key: 'innovative',
            title: 'Innovative Digital Solutions',
            paragraph:
                'N-Vision Digital Dental Laboratory is at the forefront of digital dentistry revolution. By embracing advanced digital techniques such as CAD/CAM design, 3D printing, and computer-guided milling, we have streamlined the dental restoration process, resulting in faster turnaround times without compromising on quality. Our digital solutions also provide an enhanced level of accuracy, ensuring a seamless fit and optimal comfort for patients.'
        },
        {
            order: 2,
            key: 'personalized',
            title: 'Partners in Dental Excellence',
            paragraph:
                'N-Vision Digital Dental Laboratory is more than a laboratory; we are your partners in achieving dental excellence. Our commitment extends beyond producing dental restorations – we aim to elevate the standards of modern dentistry by providing continuous education and support to dental professionals. Together, we can shape the future of oral healthcare, one radiant smile at a time.'
        },
        {
            order: 3,
            key: 'quality',
            title: 'Quality Without Compromise',
            paragraph:
                'Quality is the cornerstone of our work philosophy. We use only the finest FDA-approved materials, combined with our mastery of digital design and traditional craftsmanship, to deliver dental restorations that surpass expectations. Rigorous quality control processes at every stage of production guarantee that every restoration meets our high standards, providing both dental professionals and patients with the confidence they deserve.'
        },
        {
            order: 4,
            key: 'experience',
            title: 'Experience the N-Vision Difference',
            paragraph:
                'Elevate your dental practice with N-Vision Digital Dental Laboratory. Discover the synergy of artistry and technology that transforms smiles and enhances lives. With a legacy of precision, innovation, and unwavering dedication, we invite you to experience the N-Vision difference and join us on a journey to dental perfection. Your vision, our expertise – a masterpiece in every smile.'
        }
    ]
};

// Copy + images for the /services page (Task 8), moved verbatim from
// src/pages/public/service/{hero,aligners,dentalImplants,crownsbridges,laminates,
// Partialdenter,aorthdontix}.jsx - not one word of the headings/paragraphs/claims reworded.
// Unlike `about` above, the six service components stay as separate files (per the task-8
// brief's file list), so this is read by six components rather than mapped in one place;
// each looks up its own entry by `key`.
//
// `alt: true` marks the three services that were image-left/dark-background in the old
// design (Dental Implants, Veneers, Orthodontics) - reused here to drive the new alternating
// two-column layout (photos-left + tinted section background) so the already-alternating
// structure of the source data carries over exactly, rather than inventing a new order.
// `id` carries the three anchor ids that exist in the current source (implants, cnb,
// orthodontics - see crownsbridges.jsx/dentalImplants.jsx/aorthdontix.jsx) plus matching ids
// added to the other three services for consistency (aligners, veneers, dentures) - nothing
// in this app links to any of the six, but they are live, possibly-bookmarked URLs.
export const serviceDetails = {
    hero: {
        // Reuses the existing 'Our services' string verbatim (sections.services.eyebrow,
        // already rendered on the Home page) rather than inventing a new eyebrow label -
        // same reasoning as `about.hero.eyebrow` reusing `sections.hero.eyebrow` above.
        eyebrow: sections.services.eyebrow,
        title: 'Services',
        paragraph:
            'Our cutting-edge dental technologies and advanced digital techniques allow us to provide efficient and effective solutions.',
        claimsTitle: 'Claim your first free unit!',
        claims: [
            'FREE fast pickup & delivery',
            'Best quality for the lowest prices',
            'Fully integrated online system',
            'We Accept All Digital Scans',
            'Submit & track cases online 24/7'
        ]
    },
    services: [
        {
            order: 0,
            key: 'aligners',
            id: 'aligners',
            alt: false,
            title: 'Aligners',
            // Middle line carries the NVISALIGN brand mark (bold, with the 'NVIS' half in the
            // accent colour) in the original markup - `brandLine: true` tells aligners.jsx to
            // render that one line with the same <b><span>NVIS</span>ALIGN</b> treatment
            // instead of plain text, everything else here is plain verbatim copy.
            paragraph: [
                'Aligners are orthodontic devices based on a series of transparent aligner to realign the patient’s teeth as an alternative to wires and brackets as in regular dental braces.',
                'Aligner technology is an alternative to orthodontic braces. NVISALIGN is one example of this technology.',
                'It uses thin, transparent plastic shells to move teeth in small increments when worn consistently.'
            ],
            images: [
                { src: AlignersA, alt: 'Clear aligner tray being fitted against the upper teeth' },
                { src: AlignersB, alt: 'Clear aligner tray worn over the lower and upper teeth, close up' }
            ]
        },
        {
            order: 1,
            key: 'implants',
            id: 'implants',
            alt: true,
            title: 'Dental Implants',
            paragraph: [
                'Dental Implants are medical devices surgically implanted into the jaw to restore a person\'s ability to chew or their appearance. They provide support for artificial teeth, such as crowns, bridges, or dentures.',
                'Dental implant systems consist of a dental implant body and dental implant abutment and may also include an abutment fixation screw.'
            ],
            images: [
                { src: ImplantsA, alt: 'Illustration of two dental implants restoring missing molars in the lower jaw' },
                { src: ImplantsB, alt: 'Illustration of a single dental implant, abutment, and crown above a gap in the jaw' }
            ]
        },
        {
            order: 2,
            key: 'cnb',
            id: 'cnb',
            alt: false,
            title: 'Crowns & Bridges',
            paragraph: [
                'Crowns & Bridges are dental restorations that covers the exposed surface of a tooth to strengthen it or improve its appearance.',
                'A bridge is a dental restoration to replace one or more missing teeth. It includes an artificial tooth or teeth which are fused to crowns on either side to provide support.'
            ],
            images: [
                { src: CnbA, alt: 'Illustration of a dental crown being placed over a prepared tooth' },
                { src: CnbB, alt: 'Illustration of a dental bridge spanning two prepared abutment teeth' }
            ]
        },
        {
            order: 3,
            key: 'veneers',
            id: 'veneers',
            alt: true,
            title: 'Veneers',
            paragraph: [
                'Veneers are thin coverings that are placed over the front part of your tooth that look like natural teeth. Veneers can be used to correct a wide range of dental issues, included stained, chipped, crooked or damaged teeth. They can even help conceal uneven spaces between teeth.'
            ],
            images: [
                { src: VeneersA, alt: 'Illustration of a porcelain veneer shell fitted over a front tooth' },
                { src: VeneersB, alt: 'Close up illustration of veneer shells being placed on the front teeth' }
            ]
        },
        {
            order: 4,
            key: 'dentures',
            id: 'dentures',
            alt: false,
            title: 'Removeable Dentures',
            paragraph: [
                'Removable dentures, better known as false teeth, are prosthetic teeth made from material such as acrylic and metal, which fit over the gums and/or remaining teeth to give the appearance that the patient still has a full set of teeth.'
            ],
            images: [
                { src: DenturesA, alt: 'Removable partial denture with a cast metal framework and clasps' },
                { src: DenturesB, alt: 'Illustration of a removable partial denture replacing missing lower teeth' }
            ]
        },
        {
            order: 5,
            key: 'orthodontics',
            id: 'orthodontics',
            alt: true,
            title: 'Orthodontics',
            paragraph: [
                'Orthodontics is a dental specialty focused on aligning your bite and straightening your teeth. You might need to see an orthodontist if you have crooked, overlapped, twisted or gapped teeth. Common orthodontic treatments include traditional braces, clear aligners and removable retainers.'
            ],
            images: [
                { src: AlignersA, alt: 'Clear aligner tray being fitted against the upper teeth' },
                { src: OrthodontixB, alt: 'Clear plastic orthodontic retainer tray' }
            ]
        }
    ]
};

export const services = [
    {
        order: 0,
        name: 'Crowns & Bridges',
        icon: 'crown',
        description:
            'Full-contour and layered crowns milled from zirconia or e.max. Single-unit to multi-unit bridges, designed for a precise, comfortable fit.',
        tags: ['Zirconia', 'e.max']
    },
    {
        order: 1,
        name: 'Implant Restorations',
        icon: 'implant',
        description:
            'Custom abutments, implant crowns, and screw-retained restorations compatible with all major implant systems.',
        tags: ['Straumann', 'Nobel Biocare', 'Zimmer']
    },
    {
        order: 2,
        name: 'Veneers & Smile Design',
        icon: 'veneer',
        description:
            'Ultra-thin e.max veneers and full digital smile design packages with diagnostic wax-ups and try-in mock-ups.',
        tags: ['e.max Press', 'Lithium disilicate']
    },
    {
        order: 3,
        name: 'Dentures & Partials',
        icon: 'denture',
        description:
            'Full and partial dentures including digital dentures for faster, more accurate fabrication with improved patient outcomes.',
        tags: ['Full denture', 'Partial RPD', 'Flexible']
    },
    {
        order: 4,
        name: 'Nightguards & Splints',
        icon: 'nightguard',
        description:
            'Hard acrylic, soft, and dual-laminate occlusal guards. Sleep appliances and Michigan splints for bruxism management.',
        tags: ['Hard acrylic', 'Dual-laminate']
    },
    {
        order: 5,
        name: '3D Printing & CAD/CAM',
        icon: 'printer',
        description:
            'In-house CAD design, 5-axis milling, and SLA/DLP 3D printing for models, surgical guides, and temporary restorations.',
        tags: ['Surgical guides', 'Study models', 'Temps']
    }
];

export const steps = [
    { order: 0, num: '01', title: 'Submit your case', description: 'Log in to the client portal and upload your digital scan files or ship physical impressions. Add prescription details and shade.' },
    { order: 1, num: '02', title: 'CAD design review', description: 'Our technicians design the restoration in 3Shape or exocad. Complex cases include a design preview for your approval.' },
    { order: 2, num: '03', title: 'Mill & fabricate', description: 'Restorations are milled, stained, glazed, and quality-checked against your prescription before packaging.' },
    { order: 3, num: '04', title: 'Delivery & tracking', description: 'Every case is packed securely and dispatched with full tracking. Your portal updates in real time so you know exactly when to expect each case.' }
];

export const features = [
    { order: 0, icon: 'upload', title: 'Digital case submission', description: 'Upload STL files directly from your intraoral scanner — no physical impressions required.' },
    { order: 1, icon: 'clock', title: 'Real-time order tracking', description: 'See exactly where your case is — scanning, designing, milling, or out for delivery.' },
    { order: 2, icon: 'invoice', title: 'Invoices & case history', description: 'Access your full billing history, past prescriptions, and lab slips at any time.' }
];

export const testimonials = [
    { order: 0, quote: "The fit quality is consistently excellent. We switched from our previous lab eight months ago and haven't had a single remake. The digital portal makes case tracking effortless.", authorName: 'Dr. Salman Arif', authorRole: 'General Dentist, Lahore', initials: 'SA' },
    { order: 1, quote: 'We send full-arch implant cases and the precision is outstanding. Turnaround is exactly what they promise. The team responds quickly and the zirconia aesthetics are superb.', authorName: 'Dr. Fatima Hassan', authorRole: 'Prosthodontist, Karachi', initials: 'FH' },
    { order: 2, quote: 'Finally a lab that works digitally. No more lost impressions or delayed cases. The smile design previews they send before fabrication have become a selling point for our practice.', authorName: 'Dr. Mehwish Raza', authorRole: 'Cosmetic Dentist, Islamabad', initials: 'MR' }
];

// Street addresses (`lines`) are verbatim from the seed. `phone`/`email` are not in the
// seed (it only ever carried one placeholder contact block) - they are the real per-branch
// values, cross-checked against src/pages/public/home/labs/index.jsx (the current live
// source of truth), matched to each office in the same Charlotte / Lahore / Faisalabad order.
// Task 5 fix: Lahore's and Faisalabad's postal codes (54782 / 38000) were missing from the
// seed-derived `lines` here even though labs/index.jsx (the live source of truth) carries
// them - added to close that gap, since the new Addresses.jsx renders straight from here.
export const addresses = [
    { order: 0, name: 'Charlotte Office', badge: 'Head Office', isHq: true, lines: ['7561 Orr Rd.,', 'Charlotte, NC 28213,', 'USA'], phone: '+1 (845) 544 - 0100', email: 'usa@nvisiondental.com' },
    { order: 1, name: 'Lahore Office', badge: 'Regional Office', isHq: false, lines: ['P-Block, Oman Road,', 'Johar Town,', 'Lahore, 54782, Pakistan'], phone: '+92 (333) 460 - 1314', email: 'lhr@nvisiondental.com' },
    { order: 2, name: 'Faisalabad Office', badge: 'Regional Office', isHq: false, lines: ['P-369, B-Block, St. 2,', 'Al-Najaf Colony,', 'Faisalabad, 38000, Pakistan'], phone: '+92 (300) 460 - 1314', email: 'fsd@nvisiondental.com' }
];


export const heroStats = [
    { order: 0, value: '5–7', label: 'Day turnaround' },
    { order: 1, value: '100%', label: 'Satisfaction guarantee' },
    { order: 2, value: 'CAD/CAM', label: 'Digital workflow' }
];

// Kept from the current site and rebuilt on the new card system. These are the only
// photographs of the lab's actual output anywhere on the site, and for a dental
// laboratory that is the evidence a prospective dentist actually judges.
// No per-image captions: src/pages/public/home/samples/index.jsx carries none (one shared
// paragraph for the whole block, not per-image), so none are invented here.
export const ourWork = {
    eyebrow: 'Our work',
    title: 'Examples of our craftsmanship',
    images: [Work1, Work2, Work3, Work4]
};

// Hero illustration: the lab's digital workflow as an animated loop. Step names follow the
// `steps` block above (How it works) so the hero and that section tell the same story; the
// centre caption echoes the "Fully digital workflow" wheel on the pre-redesign home page.
export const heroWorkflow = {
    caption: ['Fully digital', 'workflow'],
    steps: [
        { icon: 'scan', num: '01', label: 'Scan & submit' },
        { icon: 'design', num: '02', label: 'CAD design' },
        { icon: 'mill', num: '03', label: 'Milling' },
        { icon: 'deliver', num: '04', label: 'Delivery' }
    ]
};

// /contant (Task 10). `hero`/`email` copied verbatim from the old
// src/pages/public/contact/{hero,email}.jsx (git 06da2a1). `form` labels are the exact
// strings the old contact/form.jsx passed to Primaryinput/PrimarytextArea/Primaryaltbtn as
// `Lable`/`Value` - including "You thoughts" (not "Your thoughts"), which is the live copy,
// not a typo introduced here. `errors`/`success` are new: the old form had no validation and
// no submit handler at all (Task 10's own finding - see task-10-report.md), so there is no
// prior wording to preserve for them. They are functional status copy, not marketing copy,
// and make no promises the page can't keep (no response-time, no confirmation of delivery -
// mailto: hands off to the visitor's own mail client, which is all this page can honestly
// claim).
export const contact = {
    hero: {
        title: 'Customer Support',
        paragraph:
            "We're here to assist you and provide the support you need. Whether you have questions, concerns, or require assistance with our products or services, our dedicated team is ready to help."
    },
    form: {
        firstNameLabel: 'First name',
        lastNameLabel: 'Last name',
        emailLabel: 'Email',
        messageLabel: 'You thoughts',
        submitLabel: 'Submit',
        errors: {
            firstName: 'Please enter your first name.',
            lastName: 'Please enter your last name.',
            email: 'Please enter your email address.',
            message: 'Please enter a message.'
        },
        success:
            "Your email client should now be open with your message ready to send. If it didn't open, email us directly at info@nvisiondental.com."
    },
    email: {
        paragraph:
            'At our company, customer satisfaction is our top priority. We strive to ensure that your experience with us is seamless and enjoyable. Our knowledgeable support staff is well-equipped to address your inquiries and provide prompt solutions.',
        cta: 'Send an email'
    }
};

// /faq (Task 9). Hero title copied verbatim from the old src/pages/public/faq/hero.jsx -
// that file had no eyebrow or lead paragraph (unlike about.hero/serviceDetails.hero), so
// none is invented here; the hero renders title-only inside .hero-section/.about-hero-inner.
//
// `items` is every question/answer pair from the old src/pages/public/faq/items.jsx's QNAs
// array, copied verbatim (wording, punctuation, order) - confirmed against git history
// (af0b768) to be the complete original set, seven pairs, not thirteen.
export const faq = {
    hero: {
        title: 'Frequently Asked Questions'
    },
    items: [
        {
            question: 'What is a N Vision Digital Dental Lab?',
            answer: 'N Vision Digital Dental Lab is a specialized facility where dental technicians create custom dental restorations, such as crowns, bridges, dentures, and veneers, based on the specifications provided by dentists. These restorations are fabricated to improve the function, aesthetics, and overall oral health of patients.'
        },
        {
            question: 'How does a N Vision Digital Dental Lab work?',
            answer: "After receiving an order from a dentist, the dental lab technicians follow a precise process. They start by taking impressions or digital scans of the patient's teeth and gums. These impressions or scans are then used to create custom restorations in a dental lab. The technicians craft the restorations using high-quality materials, such as porcelain or composite, and employ various techniques like milling, shaping, and color matching. Finally, the completed restorations are sent back to the dentist for placement in the patient's mouth."
        },
        {
            question: 'What types of dental restorations can N Vision Digital Dental Lab create?',
            answer: 'N Vision Digital Dental Lab can create a wide range of dental restorations, including crowns, bridges, dentures, veneers, inlays, and onlays. These restorations can be made from different materials, such as porcelain, zirconia, or acrylic, depending on the specific needs and preferences of the patient.'
        },
        {
            question: 'How long does it take for N Vision Digital Dental Lab to complete a restoration?',
            answer: 'The time required to complete a dental restoration can vary depending on the complexity of the case and the specific procedures involved. Typically, a restoration can take anywhere from a few days to a couple of weeks. However, advancements in technology and digital dentistry have reduced turnaround times in many cases, allowing for quicker delivery of restorations.'
        },
        {
            question: 'How can I ensure the quality of restorations from N Vision Digital Dental Lab?',
            answer: 'To ensure the quality of dental restorations, we hire reputable, experienced and skilled technicians. Look for certifications, accreditations, and testimonials from dental professionals who have worked with the lab. Additionally, communication and collaboration between the dentist and N Vision Digital Dental Lab are crucial to achieving optimal results.'
        },
        {
            question: 'Can N Vision Digital Dental Lab repair or modify existing dental restorations?',
            answer: "Yes, N Vision Digital Dental Lab can often repair or modify existing dental restorations. If you have a damaged or ill-fitting dental restoration, consult with your dentist who can work in partnership with the N Vision Digital Dental Lab to assess the restoration's condition and determine the appropriate course of action for repair or modification."
        },
        {
            question: 'How should I communicate with N Vision Digital Dental Lab?',
            answer: "Communication with N Vision Digital Dental Lab is typically facilitated through the dentist's office. Your dentist will provide the lab with the necessary information and instructions for your specific case. If you have any questions or concerns, it is best to discuss them with your dentist, who can then liaise with the dental lab on your behalf."
        }
    ]
};

// /privacypolicy (Task 11). Copied verbatim - not one word reworded - from the old
// src/pages/public/privacypolicy/{hero,content}/index.jsx (git 8cc2307). The old hero was
// a title + "Last updated" line + one lead paragraph; the old content was eight <h3>-headed
// blocks in document order, two of them followed by a <ul> and none of them numbered (the
// source itself carries no clause numbers, so none is added here per the task brief).
//
// The old "Contact Us" block held two sentences separated by a <br/> inside one <p>, not
// two separate paragraphs - `paragraph` is an array of the same two sentences here (same
// shape services.jsx already uses for a multi-line block) so it renders as two <p>s instead
// of one <p> with an inline break. This changes markup only, not a single word of the text.
export const privacy = {
    hero: {
        title: 'Privacy Policy for N Vision Digital Dental Laboratory',
        lastUpdated: 'Last updated: [16/05/2020]',
        intro:
            'N Vision Digital Dental Laboratory ("us", "we", or "our") is committed to safeguarding your privacy. This Privacy Policy outlines how we collect, use, disclose, and protect your information when you use our digital dental design and manufacturing services ("Services"). By using our Services, you consent to the practices described in this policy.'
    },
    sections: [
        {
            key: 'collect',
            title: 'Information We Collect',
            paragraph: 'We may collect personal information from dental professionals and dental clinics (our "Clients") to provide and improve our Services. This information may include:',
            list: [
                'Contact Information: Names, email addresses, phone numbers, and postal addresses.',
                'Professional Details: Professional titles, dental practice affiliations, and credentials.',
                'Transaction Information: Information related to orders, payments, and invoices.',
                'Technical Information: Usage details, IP addresses, browser types, and device identifiers.'
            ]
        },
        {
            key: 'use',
            title: 'How We Use Your Information',
            paragraph: 'We use the collected information for various purposes, including:',
            list: [
                'Providing and improving our Services, including digital dental design and manufacturing.',
                'Communicating with you about orders, inquiries, and updates.',
                'Enhancing user experience and customer support.',
                'Complying with legal obligations and protecting our rights.'
            ]
        },
        {
            key: 'sharing',
            title: 'Information Sharing and Disclosure',
            paragraph: 'We do not share personal information with third parties for their direct marketing purposes. However, we may share information with service providers and business partners who assist in our operations. Your information may also be shared to comply with legal obligations or to protect our rights and the rights of others.'
        },
        {
            key: 'security',
            title: 'Data Security',
            paragraph: 'We employ industry-standard measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. Despite our efforts, no data transmission over the Internet or electronic storage method is entirely secure. Therefore, we cannot guarantee absolute security.'
        },
        {
            key: 'choices',
            title: 'Your Choices',
            paragraph: 'You have the right to access, correct, and delete your personal information. You can update your preferences regarding communications from us. To, exercise these rights or for any inquiries, please contact us at [privacy@nvisiondental.com].'
        },
        {
            key: 'cookies',
            title: 'Cookies and Tracking Technologies',
            paragraph: 'We may use cookies and similar technologies to enhance your experience on our website. You can modify your browser settings to refuse cookies or notify you when they are being used.'
        },
        {
            key: 'changes',
            title: 'Changes to This Privacy Policy',
            paragraph: 'We may update this Privacy Policy to reflect changes in our practices or legal requirements. Updates will be posted on our website and the "Last updated" date will be revised accordingly.'
        },
        {
            key: 'contactus',
            title: 'Contact Us',
            paragraph: [
                'For any questions, concerns, or requests regarding this Privacy Policy, please contact our privacy team at [privacy@nvisiondental.com].',
                'Please ensure that you review and adapt this privacy policy according to your specific business practices and legal requirements. If you have any further questions or need additional assistance, feel free to ask.'
            ]
        }
    ]
};
